import { useState } from 'react'
import { ExternalLink, Loader2, Send, Sparkles } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { Ticket } from '@/types/ticket'
import { useAdminPatch } from '@/queries/ticketMutations'
import { ticketKeys } from '@/queries/keys'
import { useInvestigations, useStartInvestigation } from '@/queries/investigations'
import { isStale } from '@/types/investigation'
import { StatusSelect } from './StatusSelect'
import { PrioritySelect } from './PrioritySelect'
import { AssigneeCombobox } from './AssigneeCombobox'
import { TrackerControls } from './TrackerControls'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { toast } from 'sonner'

interface ExtendedTicket extends Ticket {
  linearIssueUrl?: string | null
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[68px_1fr] items-center gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

export function AdminControls({ ticket }: { ticket: ExtendedTicket }) {
  const patch = useAdminPatch(ticket._id)
  const qc = useQueryClient()
  const [linearUrl, setLinearUrl] = useState<string | null>(ticket.linearIssueUrl ?? null)

  const apply = async (input: Parameters<typeof patch.mutateAsync>[0]) => {
    try { await patch.mutateAsync(input) }
    catch (err) { toast.error((err as Error).message ?? 'Update failed') }
  }

  const linearMutation = useMutation({
    mutationFn: () => api<{ url: string; alreadyLinked: boolean; title?: string }>(`/support-tickets/${ticket._id}/linear`, { method: 'POST', body: {} }),
    onSuccess: data => {
      setLinearUrl(data.url)
      qc.invalidateQueries({ queryKey: ticketKeys.detail(ticket._id) })
      toast.success(data.alreadyLinked ? 'Already linked to Linear' : `Linear issue created: ${data.title ?? 'see link'}`)
    },
    onError: err => toast.error((err as Error).message ?? 'Could not create Linear issue'),
  })

  const investigations = useInvestigations(ticket._id)
  const startInvestigation = useStartInvestigation(ticket._id)
  const investigating = (investigations.data?.data ?? []).some(
    r => (r.status === 'queued' || r.status === 'running') && !isStale(r),
  )

  return (
    <section className="px-5 py-4 space-y-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Admin</h3>

      <div className="space-y-2">
        <Field label="Status">
          <StatusSelect value={ticket.status} disabled={patch.isPending} onChange={s => apply({ status: s })} />
        </Field>
        <Field label="Priority">
          <PrioritySelect value={ticket.priority} disabled={patch.isPending} onChange={p => apply({ priority: p })} />
        </Field>
        <Field label="Assignee">
          <AssigneeCombobox value={ticket.assignedTo} onChange={id => apply({ assignedTo: id })} />
        </Field>
      </div>

      <div className="border-t pt-3">
        <Field label="Linear">
          {linearUrl ? (
            <a
              href={linearUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-foreground hover:underline"
            >
              View issue <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-full justify-start text-xs"
              disabled={linearMutation.isPending}
              onClick={() => linearMutation.mutate()}
            >
              {linearMutation.isPending
                ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Drafting…</>
                : <><Send className="mr-1.5 h-3.5 w-3.5" />Create issue</>}
            </Button>
          )}
        </Field>
      </div>

      <div className="border-t pt-3">
        <Field label="AI">
          <Button
            size="xs"
            variant="outline"
            className="w-full justify-start"
            disabled={investigating}
            onClick={() => startInvestigation.mutate()}
          >
            {investigating
              ? <><Loader2 className="animate-spin" />Investigating…</>
              : <><Sparkles />Investigate</>}
          </Button>
        </Field>
      </div>

      <TrackerControls ticket={ticket} />
    </section>
  )
}
